/** Performance tests in the web app: load plan wording and preview, verdict wording, number formatting. */
import { withRampRates, scenarioWorkloads, shareOut, shapeStages, totalWorkload, transactionLabel, workloadPeak, type LoadShape, type PerfConfig, type RampRate, type PerfVerdict, type Slo, type WorkloadModel } from '@1qa/shared';
import type { Tone } from '@/components/ui';

/** "50 users for 30 minutes", "Up to 3 arrivals per second for 10 min", "Check run: 1 user, once". */
export function describeModel(m: WorkloadModel): string {
  if (m.kind === 'iterations') return m.iterations === 1 ? (m.vus === 1 ? 'Check run: 1 user, once' : `Check run: each scenario once`) : `${m.vus} users, ${m.iterations} times each`;
  const peak = workloadPeak(m);
  const mins = formatMinutes(peak.durationS);
  if (m.kind === 'open') {
    const perMinute = m.timeUnit === 'm';
    const top = Math.max(...m.stages.map((s) => s.rate));
    return `Up to ${formatNumber(top)} new users per ${perMinute ? 'minute' : 'second'} for ${mins} (at most ${formatNumber(m.maxVus)} at once)`;
  }
  return `Up to ${formatNumber(peak.vus)} users for ${mins}`;
}

/** Seconds the shape runs (stages or ramp-up + steady + ramp-down). */
export const loadDurationS = (load: LoadShape) => shapeStages(load).reduce((n, s) => n + s.durationS, 0);

/** "100 users: 3 min up, 20 min steady, 1 min down" or "30 new users per minute: …"; own stages: "Own shape: …". */
export function describeLoad(load: LoadShape): string {
  const who = load.mode === 'users' ? `${formatNumber(load.target)} users` : `${formatNumber(load.target)} new users per ${load.timeUnit === 'm' ? 'minute' : 'second'}`;
  if (load.stages) return `Own shape up to ${who.replace(/^\S+ /, `${formatNumber(Math.max(...load.stages.map((s) => s.target)))} `)}, ${load.stages.length} stages, ${formatMinutes(loadDurationS(load))}`;
  // Users given a start / stop rate: "start 5 per second (20 s)".
  const ramp = (verb: string, s: number, rate?: RampRate) =>
    s ? (load.mode === 'users' && rate ? `${verb} ${formatNumber(rate.users)} per ${rate.per === 'm' ? 'minute' : 'second'} (${formatMinutes(s)})` : `${formatMinutes(s)} ramp-${verb === 'start' ? 'up' : 'down'}`) : '';
  const parts = [ramp('start', load.rampUpS, load.rampUpRate), load.steadyS && `${formatMinutes(load.steadyS)} steady`, ramp('stop', load.rampDownS, load.rampDownRate)].filter(Boolean);
  return `${who}: ${parts.join(', ')}`;
}

/**
 * The load of each scenario over time, for the preview chart: users (or new users per second) every `stepS` seconds,
 * as k6 ramps it (linear between stage ends), plus the total.
 */
export function loadPreview(plan: Pick<PerfConfig, 'load' | 'scenarios'>, iterationSeconds: Record<string, number> = {}) {
  const parts = scenarioWorkloads(plan, iterationSeconds);
  const total = totalWorkload(parts);
  const end = workloadPeak(total).durationS;
  const stepS = Math.max(5, Math.ceil(end / 150 / 5) * 5);
  const valueAt = (m: WorkloadModel, start: number, t: number) => {
    if (m.kind === 'iterations' || t < start) return null;
    let x = start;
    let prev = 0;
    for (const st of m.stages) {
      const v = 'vus' in st ? st.vus : st.rate / (m.kind === 'open' && m.timeUnit === 'm' ? 60 : 1);
      if (t <= x + st.durationS) return prev + ((v - prev) * (t - x)) / st.durationS;
      x += st.durationS;
      prev = v;
    }
    return null;
  };
  const times = Array.from({ length: Math.floor(end / stepS) + 1 }, (_, i) => i * stepS);
  const series = plan.scenarios.map((sc, i) => ({ id: sc.id, name: sc.name, values: times.map((t) => valueAt(parts[i]!.model, parts[i]!.startAfterS, t)) }));
  const sum = times.map((_, k) => series.reduce((n, s) => n + (s.values[k] ?? 0), 0));
  return { stepS, series, total: sum, durationS: end, peak: workloadPeak(total), model: total };
}

/** Transaction names goals can use: "Scenario › Transaction" when the test has several scenarios. */
export function planTransactions(plan: Pick<PerfConfig, 'scenarios'>, scripts: Map<string, { transactions: { name: string }[] }>): string[] {
  const multi = plan.scenarios.length > 1;
  return plan.scenarios.flatMap((sc) => (scripts.get(sc.scriptId)?.transactions ?? []).map((t) => transactionLabel(multi ? sc.name : null, t.name)));
}

/** A new scenario id: short, lower-case, unused. */
export function nextScenarioId(taken: string[]): string {
  for (let i = 1; ; i++) if (!taken.includes(`s${i}`)) return `s${i}`;
}

/** Shares that add up to 100, as evenly as whole percent allow (the first ones get the rest). */
export function evenShares(n: number): number[] {
  const base = Math.floor(100 / n);
  return Array.from({ length: n }, (_, i) => base + (i < 100 - base * n ? 1 : 0));
}

/**
 * The plan with its scenarios' shares (and, split by users, the maximum users) worked out from how the users are split:
 * equally -> even shares; by number of users -> shares from each scenario's users, the maximum is their sum.
 */
export function withSplit(plan: PerfConfig): PerfConfig {
  if (plan.split === 'even') {
    const shares = evenShares(plan.scenarios.length);
    return { ...plan, scenarios: plan.scenarios.map(({ users: _, ...sc }, i) => ({ ...sc, share: shares[i]! })) };
  }
  if (plan.split !== 'users') return { ...plan, scenarios: plan.scenarios.map(({ users: _, ...sc }) => sc) };
  const sum = plan.scenarios.reduce((n, sc) => n + (sc.users ?? 1), 0);
  return {
    ...plan,
    load: withRampRates({ ...plan.load, target: Math.max(1, sum) }),
    scenarios: plan.scenarios.map((sc) => ({ ...sc, users: sc.users ?? 1, share: Math.round(((sc.users ?? 1) / sum) * 1e6) / 1e4 })),
  };
}

/** Users each scenario gets at the peak (users mode), as the run will start them. */
export function scenarioPeaks(plan: Pick<PerfConfig, 'load' | 'scenarios'>): number[] {
  const peak = Math.max(0, ...shapeStages(plan.load).map((st) => st.target));
  return shareOut(peak, plan.scenarios.map((sc) => sc.share));
}

/** How fast a ramp of `total` users over `seconds` starts them: "about 5 users per second", "1 user every 4 s". */
export function rampPace(total: number, seconds: number): string {
  if (seconds <= 0 || total <= 0) return 'all at once';
  const perS = total / seconds;
  if (perS >= 1) return `about ${formatNumber(perS)} user${perS === 1 ? '' : 's'} per second`;
  const every = seconds / total;
  return `1 user every ${every < 90 ? `${formatNumber(every)} s` : formatMinutes(every)}`;
}

/** Where a script came from, in plain words. */
export const SCRIPT_SOURCE: Record<'har' | 'recorder' | 'functional' | 'copy', string> = {
  har: 'HAR file',
  recorder: 'Recorded in the browser',
  functional: 'From a functional test',
  copy: 'Copy',
};

export function formatMinutes(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)} s`;
  const m = seconds / 60;
  return m < 120 ? `${Math.round(m * 10) / 10} min` : `${Math.round((m / 60) * 10) / 10} h`;
}

export const formatMs = (ms: number | null | undefined) => (ms === null || ms === undefined ? '-' : ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms < 10 ? ms.toFixed(1) : Math.round(ms)} ms`);
export const formatPct = (x: number | null | undefined) => (x === null || x === undefined ? '-' : `${x === 0 ? 0 : x < 0.001 ? '<0.1' : Math.round(x * 1000) / 10} %`);
export const formatNumber = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: n < 10 ? 1 : 0 });

export const VERDICT: Record<PerfVerdict, { label: string; tone: Tone; text: string }> = {
  PASS: { label: 'Passed', tone: 'success', text: 'Every goal was met and the measurement is trustworthy.' },
  FAIL: { label: 'Goal missed', tone: 'danger', text: 'The app missed at least one goal under this load.' },
  INVALID: { label: 'Not trustworthy', tone: 'warning', text: 'The numbers can’t be trusted (for example, the load agent itself was at its limit).' },
  DEGRADED: { label: 'Partial result', tone: 'info', text: 'Goals were met, but part of the planned load or data is missing.' },
};

/** "p95 under 750 ms" / "under 1 % errors", with the scope. */
export function describeSlo(slo: Slo): string {
  const what = slo.metric === 'errorRate' ? `under ${formatPct(slo.max)} errors` : `${slo.metric} under ${formatMs(slo.max)}`;
  return slo.scope === 'all' ? what : `${what} for “${slo.scope}”`;
}

/** The two goals the simple form edits: overall p95 and error rate (other goals are kept as they are). */
export function simpleGoals(slos: Slo[]): { p95Ms: number | null; errorPct: number | null } {
  const p95 = slos.find((s) => s.metric === 'p95' && s.scope === 'all');
  const err = slos.find((s) => s.metric === 'errorRate' && s.scope === 'all');
  return { p95Ms: p95?.max ?? null, errorPct: err ? Math.round(err.max * 10000) / 100 : null };
}

export function withSimpleGoals(slos: Slo[], goals: { p95Ms: number | null; errorPct: number | null }): Slo[] {
  const rest = slos.filter((s) => !(s.scope === 'all' && (s.metric === 'p95' || s.metric === 'errorRate')));
  return [
    ...(goals.p95Ms ? [{ metric: 'p95' as const, scope: 'all', max: goals.p95Ms }] : []),
    ...(goals.errorPct !== null && goals.errorPct >= 0 ? [{ metric: 'errorRate' as const, scope: 'all', max: Math.max(goals.errorPct, 0.0001) / 100 }] : []),
    ...rest,
  ];
}

/** "nice" upper bound for an axis: 1, 2, 2.5 or 5 x 10^n at or above `max`. */
export function niceMax(max: number): number {
  if (max <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= max) return m * p;
  return 10 * p;
}
